import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string } from "../../lazy-zod.js";
import * as eagerZod from "zod/v4";
//#region src/schemas/http/space-files.ts
const SPACE_FILE_MAX_SIZE_BYTES = 250 * 1024 * 1024;
const CONTENT_DIGEST_PATTERN = /^[a-f0-9]{64}$/u;
const SPACE_FILE_RAW_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
const SPACE_FILE_MAX_TEXT_EDIT_SIZE_BYTES = 1024 * 1024;
const SPACE_FILE_MAX_NAME_LENGTH = 255;
const SPACE_FILE_MAX_FOLDER_DEPTH = 20;
const SPACE_MESSAGE_FILE_MAX_COUNT = 10;
const SPACE_COMPOSER_UPLOAD_TTL_MS = 1440 * 60 * 1e3;
const SPACE_COMPOSER_UPLOAD_OBJECT_PREFIX = "composer-uploads/v1/";
const PATH_SEPARATOR_PATTERN = /[\\/]/u;
const MEDIA_TYPE_PATTERN = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/u;
const MIME_TYPES_BY_EXTENSION = {
	".csv": "text/csv",
	".gif": "image/gif",
	".html": "text/html",
	".jpeg": "image/jpeg",
	".jpg": "image/jpeg",
	".json": "application/json",
	".md": "text/markdown",
	".markdown": "text/markdown",
	".pdf": "application/pdf",
	".png": "image/png",
	".svg": "image/svg+xml",
	".txt": "text/plain",
	".xml": "application/xml",
	".yaml": "application/yaml",
	".yml": "application/yaml"
};
const scopeIdSchema = string().trim().min(1).max(512);
const spaceFileEntryIdSchema = eagerZod.string().trim().min(1).max(128);
const spaceFileRevisionIdSchema = string().trim().min(1).max(128);
const spaceFileClientMutationIdSchema = string().trim().min(1).max(128);
const spaceFileRevisionNumberSchema = eagerZod.number().int().min(1);
const spaceFileBaseRevisionSchema = number().int().min(1).nullable();
const spaceFileSizeBytesSchema = eagerZod.number().int().min(0).max(SPACE_FILE_MAX_SIZE_BYTES);
const spaceComposerUploadIdSchema = string().uuid();
/**
* The one canonical fact distinguishing a chat upload (sent as a message
* attachment, from Web Composer or CLI `space send --file`) from an
* artifact (deliberately created or uploaded straight into Files, or
* written by an Agent). Set once at file creation and never changed by a
* later revision, update, trash, or restore.
*
* A file created before this classification existed does not stay
* unclassified: the schema migration that added this column deterministically
* backfilled every pre-existing file row to `"artifact"`, because there is no
* reliable signal in that historical data to recover a chat upload from
* deliberately created work (both wrote through the identical path before
* this column existed). A missing/`NULL` value on a file after that
* migration is exceptional/corrupt data, not normal history; every read
* path still fails closed on it — treats it as "not a known artifact" —
* rather than guessing a classification for it.
*/
const spaceFileOriginSchema = enumFactory(["artifact", "chat_upload"]);
/**
* Which files a Space Files list request includes. `"artifact"` (the
* default) hides chat uploads to keep the list focused on deliberately
* created or uploaded work; folders are always included. `"all"` also
* includes chat uploads and any file whose origin is missing/`NULL`
* (exceptional/corrupt data — see `spaceFileOriginSchema`, not normal
* historical data, which is backfilled to `"artifact"`).
*/
const spaceFileListOriginFilterSchema = enumFactory(["artifact", "all"]).default("artifact");
/**
* The explicit capability token a Center Agent / Remote Agent RPC caller
* sets to declare it understands `origin` on Space Files responses. Core's
* RPC entrypoint methods strip `origin` from the response
* (`toLegacySpaceFilesOriginWireValue`) unless the caller's request carries
* this exact token, so an already-published Remote Agent build — which has
* no field for this at all and therefore never sends it — keeps receiving
* the pre-origin response shape it already knows how to parse. This is the
* RPC-boundary analog of the `x-openmeld-space-files-origin-version` HTTP
* header Gateway checks for Web/CLI callers; see the Space Files Origin
* Classification Contract Impact Audit in
* `developing/engineering/development-workflow.md`.
*/
const SPACE_FILES_ORIGIN_CAPABILITY_TOKEN = "origin_aware";
const spaceFilesOriginCapabilitySchema = literal(SPACE_FILES_ORIGIN_CAPABILITY_TOKEN).optional();
/**
* Derives the opaque file identity for a retriable create mutation.
* Reusing the mutation identity produces the same file identity.
*/
async function buildSpaceFileEntryIdForMutation(clientMutationId) {
	return await buildSpaceFileMutationStorageId({
		clientMutationId,
		kind: "file"
	});
}
/**
* Derives one canonical file identity from the exact Composer publication.
* Both bounded request identities are validated independently before hashing.
*/
async function buildSpaceComposerPublishedFileId(input) {
	return await buildSpaceFileStorageId({
		identityParts: [
			"composer-upload",
			spaceFileClientMutationIdSchema.parse(input.clientMutationId),
			spaceComposerUploadIdSchema.parse(input.uploadId)
		],
		kind: "file"
	});
}
/**
* Derives an immutable revision identity from both the mutation and its bytes.
* Exact retries reuse the same object key, while changed bytes conflict instead
* of overwriting an already committed revision.
*/
async function buildSpaceFileRevisionIdForMutation(input) {
	const contentDigest = await sha256Hex(input.bytes);
	return await buildSpaceFileRevisionIdFromDigest({
		...input,
		contentDigest
	});
}
async function buildSpaceFileRevisionIdFromDigest(input) {
	const contentDigest = string().regex(CONTENT_DIGEST_PATTERN).parse(input.contentDigest);
	const revisionIdentity = JSON.stringify({
		baseRevision: spaceFileBaseRevisionSchema.parse(input.baseRevision),
		contentDigest,
		mimeType: spaceFileMimeTypeSchema.parse(input.mimeType),
		name: input.name === void 0 ? null : spaceFileNameSchema.parse(input.name),
		parentId: input.parentId === void 0 || input.parentId === null ? null : spaceFileEntryIdSchema.parse(input.parentId)
	});
	return await buildSpaceFileMutationStorageId({
		clientMutationId: input.clientMutationId,
		revisionIdentity,
		kind: "revision"
	});
}
async function buildSpaceFileMutationStorageId(input) {
	return await buildSpaceFileStorageId({
		identityParts: [spaceFileClientMutationIdSchema.parse(input.clientMutationId), input.revisionIdentity ?? ""],
		kind: input.kind
	});
}
async function buildSpaceFileStorageId(input) {
	const encoded = new TextEncoder().encode([
		"space-file",
		input.kind,
		...input.identityParts
	].join(":"));
	const bytes = new ArrayBuffer(encoded.byteLength);
	new Uint8Array(bytes).set(encoded);
	return `${input.kind}-${await sha256Hex(bytes)}`;
}
async function sha256Hex(bytes) {
	const digest = await crypto.subtle.digest("SHA-256", bytes);
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
const spaceFileMimeTypeSchema = eagerZod.string().trim().toLowerCase().max(255).regex(MEDIA_TYPE_PATTERN, "File type must be a valid media type.");
const spaceFileNameSchema = eagerZod.string().trim().min(1).max(255).refine((value) => !PATH_SEPARATOR_PATTERN.test(value), { message: "File names cannot contain path separators." }).refine((value) => !hasControlCharacter(value), { message: "File names cannot contain control characters." }).refine((value) => value !== "." && value !== "..", { message: "File names cannot be dot path segments." });
const spaceFileActorSchema = strictObject({
	profileId: scopeIdSchema,
	profileKind: enumFactory(["human", "agent"]),
	displayName: string().trim().min(1).max(200),
	ownerUserId: scopeIdSchema.optional()
});
const spaceFileAuditOperationSchema = enumFactory([
	"content_read",
	"content_write",
	"folder_create",
	"entry_update",
	"entry_trash",
	"entry_restore"
]);
const spaceFileAuditResultSchema = enumFactory(["succeeded", "failed"]);
const spaceFileAuditEventSchema = strictObject({
	eventId: string().trim().min(1).max(2048),
	operationId: scopeIdSchema,
	requestId: scopeIdSchema,
	organizationId: scopeIdSchema,
	spaceId: scopeIdSchema,
	entryId: spaceFileEntryIdSchema,
	revisionId: spaceFileRevisionIdSchema.nullable(),
	operation: spaceFileAuditOperationSchema,
	actorKind: enumFactory([
		"authenticated",
		"anonymous_viewer",
		"dispatch_continuation"
	]),
	actorProfileId: scopeIdSchema.nullable(),
	representedHumanProfileId: scopeIdSchema.nullable(),
	result: spaceFileAuditResultSchema,
	errorCode: string().trim().min(1).max(128).nullable(),
	occurredAtMs: number().int().nonnegative()
}).superRefine((value, context) => {
	if (value.actorKind !== "anonymous_viewer" !== (value.actorProfileId !== null)) context.addIssue({
		code: "custom",
		message: "Authenticated and dispatch actors require an actor Profile; anonymous viewers must not claim one.",
		path: ["actorProfileId"]
	});
	if (value.result === "succeeded" && value.errorCode !== null) context.addIssue({
		code: "custom",
		message: "Successful Space File audit events cannot carry an error code.",
		path: ["errorCode"]
	});
	if (value.result === "failed" && value.errorCode === null) context.addIssue({
		code: "custom",
		message: "Failed Space File audit events require an error code.",
		path: ["errorCode"]
	});
});
/** Optional for legacy/unsupported images; one complete display-oriented pair. */
const spaceFileImageDimensionsSchema = eagerZod.strictObject({
	width: eagerZod.number().int().min(1).max(32768),
	height: eagerZod.number().int().min(1).max(32768)
}).refine((value) => value.width * value.height <= 128 * 1024 * 1024, { message: "Image geometry exceeds the supported display area." });
const spaceFileRevisionSchema = strictObject({
	id: spaceFileRevisionIdSchema,
	fileId: spaceFileEntryIdSchema,
	revision: spaceFileRevisionNumberSchema,
	mimeType: spaceFileMimeTypeSchema,
	sizeBytes: spaceFileSizeBytesSchema,
	createdAt: iso.datetime({ offset: true }),
	createdBy: spaceFileActorSchema,
	imageDimensions: spaceFileImageDimensionsSchema.optional()
});
/**
* Canonical Space File revision embedded in a message. `fileId` and
* `revision` identify the immutable bytes; the remaining fields are the
* server-resolved send-time presentation snapshot.
*/
const spaceMessageFileReferenceSchema = eagerZod.strictObject({
	fileId: spaceFileEntryIdSchema,
	revision: spaceFileRevisionNumberSchema,
	name: spaceFileNameSchema,
	mimeType: spaceFileMimeTypeSchema,
	sizeBytes: spaceFileSizeBytesSchema,
	imageDimensions: spaceFileImageDimensionsSchema.optional()
});
const spaceMessageFileReferencesSchema = array(spaceMessageFileReferenceSchema).max(10);
const baseEntryShape = {
	id: spaceFileEntryIdSchema,
	spaceId: scopeIdSchema,
	parentId: spaceFileEntryIdSchema.nullable(),
	name: spaceFileNameSchema,
	path: string().startsWith("/").max(4096),
	createdAt: iso.datetime({ offset: true }),
	updatedAt: iso.datetime({ offset: true }),
	createdBy: spaceFileActorSchema,
	updatedBy: spaceFileActorSchema,
	trashedAt: iso.datetime({ offset: true }).nullable().default(null),
	trashedBy: spaceFileActorSchema.nullable().default(null)
};
const spaceFolderDescriptorSchema = strictObject({
	...baseEntryShape,
	kind: literal("folder")
});
const spaceFileDescriptorSchema = strictObject({
	...baseEntryShape,
	kind: literal("file"),
	latestRevision: spaceFileRevisionSchema,
	origin: spaceFileOriginSchema.nullable().default(null)
});
const spaceFileEntrySchema = discriminatedUnion("kind", [spaceFolderDescriptorSchema, spaceFileDescriptorSchema]);
const spaceFileBreadcrumbSchema = strictObject({
	id: spaceFileEntryIdSchema.nullable(),
	name: string().trim().min(1).max(255)
});
const spaceFileCapabilitiesSchema = strictObject({
	canRead: literal(true),
	canWrite: boolean()
});
const listSpaceFilesQuerySchema = strictObject({
	parentId: spaceFileEntryIdSchema.nullable().optional(),
	view: enumFactory(["active", "trash"]).default("active"),
	origin: spaceFileListOriginFilterSchema
});
const listSpaceFilesResponseSchema = strictObject({
	spaceId: scopeIdSchema,
	parentId: spaceFileEntryIdSchema.nullable(),
	view: enumFactory(["active", "trash"]).default("active"),
	origin: spaceFileListOriginFilterSchema,
	breadcrumbs: array(spaceFileBreadcrumbSchema),
	entries: array(spaceFileEntrySchema),
	capabilities: spaceFileCapabilitiesSchema
});
const getSpaceFileResponseSchema = strictObject({ file: spaceFileDescriptorSchema });
const readSpaceFileTextResponseSchema = strictObject({
	file: spaceFileDescriptorSchema,
	revision: spaceFileRevisionNumberSchema,
	content: string()
});
const listSpaceFileRevisionsResponseSchema = strictObject({
	file: spaceFileDescriptorSchema,
	revisions: array(spaceFileRevisionSchema)
});
const createSpaceFolderBodySchema = strictObject({
	name: spaceFileNameSchema,
	parentId: spaceFileEntryIdSchema.nullable().optional(),
	clientMutationId: spaceFileClientMutationIdSchema
});
const createSpaceFolderResponseSchema = strictObject({ folder: spaceFolderDescriptorSchema });
const updateSpaceFileEntryBodySchema = strictObject({
	name: spaceFileNameSchema.optional(),
	parentId: spaceFileEntryIdSchema.nullable().optional(),
	clientMutationId: spaceFileClientMutationIdSchema
}).refine((value) => value.name !== void 0 || value.parentId !== void 0, { message: "A new name or folder is required." });
const updateSpaceFileEntryResponseSchema = strictObject({ entry: spaceFileEntrySchema });
const trashSpaceFileEntryBodySchema = strictObject({ clientMutationId: spaceFileClientMutationIdSchema });
const trashSpaceFileEntryResponseSchema = strictObject({
	entryId: spaceFileEntryIdSchema,
	trashedAt: iso.datetime({ offset: true })
});
const restoreSpaceFileEntryBodySchema = strictObject({ clientMutationId: spaceFileClientMutationIdSchema });
const restoreSpaceFileEntryResponseSchema = strictObject({ entry: spaceFileEntrySchema });
const restoreSpaceFileRevisionBodySchema = strictObject({
	baseRevision: spaceFileRevisionNumberSchema,
	clientMutationId: spaceFileClientMutationIdSchema
});
const restoreSpaceFileRevisionResponseSchema = strictObject({ file: spaceFileDescriptorSchema });
const queryIntegerSchema = string().trim().regex(/^\d+$/u).transform(Number);
const createSpaceFileUploadQuerySchema = strictObject({
	name: spaceFileNameSchema,
	parentId: spaceFileEntryIdSchema.optional(),
	clientMutationId: spaceFileClientMutationIdSchema,
	origin: spaceFileOriginSchema.optional()
});
const updateSpaceFileContentQuerySchema = strictObject({
	baseRevision: queryIntegerSchema.pipe(spaceFileRevisionNumberSchema),
	clientMutationId: spaceFileClientMutationIdSchema
});
const uploadSpaceFileResponseSchema = strictObject({ file: spaceFileDescriptorSchema });
/**
* Temporary bytes selected in a Composer. This is deliberately not a Space
* File descriptor: the upload becomes a canonical root Space File only when
* the user sends the message.
*/
const spaceComposerUploadDescriptorSchema = strictObject({
	id: spaceComposerUploadIdSchema,
	name: spaceFileNameSchema,
	mimeType: spaceFileMimeTypeSchema,
	sizeBytes: spaceFileSizeBytesSchema,
	expiresAt: iso.datetime({ offset: true })
});
const stageSpaceComposerUploadQuerySchema = strictObject({ name: spaceFileNameSchema });
const stageSpaceComposerUploadResponseSchema = strictObject({ upload: spaceComposerUploadDescriptorSchema });
const publishSpaceComposerUploadBodySchema = strictObject({ clientMutationId: spaceFileClientMutationIdSchema });
const publishSpaceComposerUploadResponseSchema = strictObject({ file: spaceFileDescriptorSchema });
const discardSpaceComposerUploadResponseSchema = strictObject({
	uploadId: spaceComposerUploadIdSchema,
	discarded: literal(true)
});
const resolveSpaceComposerUploadScopeResponseSchema = strictObject({
	organizationId: scopeIdSchema,
	actorProfileId: scopeIdSchema
});
const spaceFileContentQuerySchema = strictObject({ revision: queryIntegerSchema.pipe(spaceFileRevisionNumberSchema).optional() });
const SPACE_FILE_ERROR_CODES = [
	"space_file.conflict",
	"space_file.folder_not_empty",
	"space_file.invalid_parent",
	"space_file.name_conflict",
	"space_file.not_found",
	"space_file.storage_failed",
	"space_file.too_large",
	"space_file.type_unsupported"
];
const spaceFileMutationErrorCodeSchema = enumFactory(SPACE_FILE_ERROR_CODES);
const spaceFileMutationErrorResponseSchema = strictObject({ error: strictObject({
	code: spaceFileMutationErrorCodeSchema,
	message: string().trim().min(1).max(500),
	currentFile: spaceFileDescriptorSchema.optional()
}) });
const prepareSpaceFileWriteInputSchema = strictObject({
	spaceId: scopeIdSchema,
	fileId: spaceFileEntryIdSchema,
	revisionId: spaceFileRevisionIdSchema,
	name: spaceFileNameSchema.optional(),
	parentId: spaceFileEntryIdSchema.nullable().optional(),
	baseRevision: spaceFileBaseRevisionSchema,
	clientMutationId: spaceFileClientMutationIdSchema,
	mimeType: spaceFileMimeTypeSchema,
	sizeBytes: spaceFileSizeBytesSchema,
	origin: spaceFileOriginSchema.optional()
});
const prepareSpaceFileWriteResponseSchema = strictObject({
	organizationId: scopeIdSchema,
	spaceId: scopeIdSchema,
	fileId: spaceFileEntryIdSchema,
	revisionId: spaceFileRevisionIdSchema,
	objectKey: string().trim().min(1).max(4096),
	committedFile: spaceFileDescriptorSchema.optional()
});
const commitSpaceFileWriteInputSchema = prepareSpaceFileWriteInputSchema;
const resolveSpaceFileContentInputSchema = strictObject({
	spaceId: scopeIdSchema,
	fileId: spaceFileEntryIdSchema,
	revision: spaceFileRevisionNumberSchema.optional()
});
const resolveSpaceFileContentResponseSchema = strictObject({
	file: spaceFileDescriptorSchema,
	revision: spaceFileRevisionSchema,
	organizationId: scopeIdSchema,
	objectKey: string().trim().min(1).max(4096)
});
function buildSpaceFileObjectKey(input) {
	return `org/${input.organizationId}/space/${input.spaceId}/files/${input.fileId}/revisions/${input.revisionId}`;
}
function buildSpaceComposerUploadObjectKey(input) {
	const uploadId = spaceComposerUploadIdSchema.parse(input.uploadId);
	return `${SPACE_COMPOSER_UPLOAD_OBJECT_PREFIX}org/${encodeURIComponent(input.organizationId)}/space/${encodeURIComponent(input.spaceId)}/profile/${encodeURIComponent(input.actorProfileId)}/${uploadId}`;
}
function isSpaceFileTextEditable(input) {
	return input.sizeBytes <= 1048576 && (input.mimeType.startsWith("text/") || input.mimeType === "application/json" || input.mimeType === "application/yaml" || input.mimeType === "application/x-yaml" || input.mimeType === "application/xml");
}
function resolveSpaceFileMimeType(input) {
	const declaredMimeType = input.declaredMimeType?.trim().toLowerCase();
	if (declaredMimeType) return declaredMimeType;
	const fileName = input.fileName.toLowerCase();
	const extensionStart = fileName.lastIndexOf(".");
	const extension = extensionStart >= 0 ? fileName.slice(extensionStart) : "";
	return MIME_TYPES_BY_EXTENSION[extension] ?? "application/octet-stream";
}
function hasControlCharacter(value) {
	return Array.from(value).some((character) => {
		const codePoint = character.codePointAt(0) ?? 0;
		return codePoint <= 31 || codePoint === 127;
	});
}
//#endregion
export { SPACE_COMPOSER_UPLOAD_OBJECT_PREFIX, SPACE_COMPOSER_UPLOAD_TTL_MS, SPACE_FILES_ORIGIN_CAPABILITY_TOKEN, SPACE_FILE_ERROR_CODES, SPACE_FILE_MAX_FOLDER_DEPTH, SPACE_FILE_MAX_NAME_LENGTH, SPACE_FILE_MAX_SIZE_BYTES, SPACE_FILE_MAX_TEXT_EDIT_SIZE_BYTES, SPACE_FILE_RAW_UPLOAD_MAX_BYTES, SPACE_MESSAGE_FILE_MAX_COUNT, buildSpaceComposerPublishedFileId, buildSpaceComposerUploadObjectKey, buildSpaceFileEntryIdForMutation, buildSpaceFileObjectKey, buildSpaceFileRevisionIdForMutation, buildSpaceFileRevisionIdFromDigest, commitSpaceFileWriteInputSchema, createSpaceFileUploadQuerySchema, createSpaceFolderBodySchema, createSpaceFolderResponseSchema, discardSpaceComposerUploadResponseSchema, getSpaceFileResponseSchema, isSpaceFileTextEditable, listSpaceFileRevisionsResponseSchema, listSpaceFilesQuerySchema, listSpaceFilesResponseSchema, prepareSpaceFileWriteInputSchema, prepareSpaceFileWriteResponseSchema, publishSpaceComposerUploadBodySchema, publishSpaceComposerUploadResponseSchema, readSpaceFileTextResponseSchema, resolveSpaceComposerUploadScopeResponseSchema, resolveSpaceFileContentInputSchema, resolveSpaceFileContentResponseSchema, resolveSpaceFileMimeType, restoreSpaceFileEntryBodySchema, restoreSpaceFileEntryResponseSchema, restoreSpaceFileRevisionBodySchema, restoreSpaceFileRevisionResponseSchema, spaceComposerUploadDescriptorSchema, spaceComposerUploadIdSchema, spaceFileActorSchema, spaceFileAuditEventSchema, spaceFileAuditOperationSchema, spaceFileAuditResultSchema, spaceFileBaseRevisionSchema, spaceFileBreadcrumbSchema, spaceFileCapabilitiesSchema, spaceFileClientMutationIdSchema, spaceFileContentQuerySchema, spaceFileDescriptorSchema, spaceFileEntryIdSchema, spaceFileEntrySchema, spaceFileImageDimensionsSchema, spaceFileListOriginFilterSchema, spaceFileMimeTypeSchema, spaceFileMutationErrorCodeSchema, spaceFileMutationErrorResponseSchema, spaceFileNameSchema, spaceFileOriginSchema, spaceFileRevisionIdSchema, spaceFileRevisionNumberSchema, spaceFileRevisionSchema, spaceFileSizeBytesSchema, spaceFilesOriginCapabilitySchema, spaceFolderDescriptorSchema, spaceMessageFileReferenceSchema, spaceMessageFileReferencesSchema, stageSpaceComposerUploadQuerySchema, stageSpaceComposerUploadResponseSchema, trashSpaceFileEntryBodySchema, trashSpaceFileEntryResponseSchema, updateSpaceFileContentQuerySchema, updateSpaceFileEntryBodySchema, updateSpaceFileEntryResponseSchema, uploadSpaceFileResponseSchema };

//# sourceMappingURL=space-files.js.map